import { useState, useEffect } from 'react';
import ModuleContainer from '../layout/ModuleContainer.jsx';
import InfoButton from '../common/InfoButton.jsx';

const SESSIONS = [
  { key: 'ASIA',   label: 'Asia',    start: 0,   end: 420,  color: '#22c55e' },
  { key: 'LONDON', label: 'London',  start: 420, end: 750,  color: '#3b82f6' },
  { key: 'PRE_NY', label: 'Pre-NY',  start: 750, end: 810,  color: '#ec4899' },
  { key: 'NY',     label: 'NY',      start: 810, end: 1200, color: '#f59e0b' },
];

const TOTAL_MINUTES = 1200; // 20 hours tracked (00:00-20:00 UTC)

function utcMinutesNow() {
  const now = new Date();
  return now.getUTCHours() * 60 + now.getUTCMinutes();
}

function formatDubaiTime(utcMinutes) {
  const dubaiMins = (utcMinutes + 240) % 1440;
  const h = Math.floor(dubaiMins / 60);
  const m = dubaiMins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default function SessionClock() {
  const [mins, setMins] = useState(utcMinutesNow());

  useEffect(() => {
    const timer = setInterval(() => setMins(utcMinutesNow()), 10000);
    return () => clearInterval(timer);
  }, []);

  const currentSession = SESSIONS.find(s => mins >= s.start && mins < s.end);
  const progressPct = Math.min(100, (mins / TOTAL_MINUTES) * 100);

  return (
    <ModuleContainer
      title={<span>Session Timeline <InfoButton title="Trading Sessions">
        <p><b>Asia (00:00-07:00 UTC)</b> — Low volume, range-setting. Establishes initial levels that London may sweep.</p>
        <p><b>London (07:00-12:30 UTC)</b> — Highest volume session. Sets the High/Low that defines the OTE retracement zone for NY.</p>
        <p><b>Pre-NY (12:30-13:30 UTC)</b> — Overlap period. London levels lock. NY profile is classified.</p>
        <p><b>NY (13:30-20:00 UTC)</b> — US market hours. OTE entries are taken here based on London's range.</p>
      </InfoButton></span>}
      subtitle={currentSession
        ? `${currentSession.label} Session Active — ${formatDubaiTime(currentSession.end)} Dubai close`
        : 'Markets Closed — Next: Asia 04:00 Dubai'}
    >
      {/* Timeline bar */}
      <div className="relative h-10 bg-korinth-bg rounded-lg overflow-hidden border border-korinth-border/50">
        {SESSIONS.map(s => {
          const left = (s.start / TOTAL_MINUTES) * 100;
          const width = ((s.end - s.start) / TOTAL_MINUTES) * 100;
          const isActive = currentSession?.key === s.key;

          return (
            <div
              key={s.key}
              className="absolute top-0 h-full flex items-center justify-center"
              style={{ left: `${left}%`, width: `${width}%` }}
            >
              <div className="absolute inset-0" style={{ backgroundColor: s.color, opacity: isActive ? 0.35 : 0.12 }} />
              <span className={`relative z-10 text-[10px] font-mono font-bold ${isActive ? 'text-white' : 'text-white/60'}`}>
                {s.label}
              </span>
            </div>
          );
        })}

        {/* Current position marker */}
        {mins < TOTAL_MINUTES && (
          <div
            className="absolute top-0 h-full w-0.5 bg-white z-10"
            style={{ left: `${progressPct}%` }}
          >
            <div className="absolute -top-0.5 -left-1 w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
          </div>
        )}
      </div>

      {/* Time labels */}
      <div className="flex justify-between mt-1.5 text-[10px] font-mono text-korinth-muted">
        {SESSIONS.map(s => (
          <span key={s.key}>{formatDubaiTime(s.start)}</span>
        ))}
        <span>{formatDubaiTime(TOTAL_MINUTES)}</span>
      </div>

      {/* Session status */}
      {currentSession && (
        <div className="flex items-center gap-2 mt-3">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: currentSession.color }} />
          <span className="text-xs font-mono" style={{ color: currentSession.color }}>
            {currentSession.label} Session Active
          </span>
          <span className="text-[10px] text-korinth-muted ml-auto">
            {Math.round((mins - currentSession.start) / (currentSession.end - currentSession.start) * 100)}% complete
          </span>
        </div>
      )}
    </ModuleContainer>
  );
}
